import { ParamsSerializerOptions } from 'axios';
import qs from 'qs';

import { client } from '@api/client';

import { DefaultVendorOptions } from '@interfaces/alarms/threshold';
import { IVendor } from '@interfaces/cmModule/dto/cmModuleDTO';
import { IOrganizationId, TNameTitle } from '@interfaces/common/common';
import {
	IAvailableGranularityDrillUpOptionsDTO,
	ICorrelateDTO,
	IDynamicDatesRange,
	IDynamicDatesRangeDTO,
	IEditPresetDTO,
	IEditPresetNameDTO,
	IExportKpiCompareDTO,
	IExportKpiCorrelateDTO,
	IExportKpiDTO,
	IExportKpiFromParamsDTO,
	IGranularitiesOptionsDTO,
	IKpiChartsDTO,
	IKpiChartsProgressDTO,
	IKpiTreeGroup,
	IMaxSelection,
	IOptionsKpiDto,
	IProgressKpiDTO,
	ISavePresetDTO,
	ISitesOptionsDTO,
	TPresetTypeDTO,
} from '@interfaces/kpi/dto/kpiDTO';
import { IChart } from '@interfaces/kpi/kpiCommon';
import { INameTitle } from '@awtg-ltd/react-map-component/dist/interfaces';
import { IGroupSelectOptions, IGroupSelectOptionsOranDto } from '@components/common/GroupSelect/GroupSelect.types';

import { TVendorsValues } from '@enums/common';
import { EGranularityValue } from '@enums/kpi-common';

import { URL } from './urls';

const {
	presets,
	sitesKpi,
	sitesKpiOran,
	vendorsKpi,
	correlationKpi,
	chartsKpiProgress,
	chartsKpiRun,
	chartsKpi,
	exportKpi,
	exportCompareKpi,
	exportCorrelateKpi,
	settingsKpi,
	groupedKpi,
	groupedCounters,
	granularitiesKpiOran,
	kpisGroupedLineType,
	dynamicDates,
} = URL;

export const getPresets = (params: Partial<IVendor>): Promise<TPresetTypeDTO[]> => client.get(presets, { params });

export const savePreset = (data: ISavePresetDTO): Promise<void> => client.post(presets, data);

export const removePreset = ({ id }: Pick<IEditPresetNameDTO, 'id'>): Promise<void> => client.post(`${presets}/${id}`, {});

export const checkPresetForUpdate = ({
	id,
	name,
}: IEditPresetNameDTO): Promise<void> => {
	return client.post(`${presets}/${id}/check-for-update`, { name });
};

export const checkPresetForUpdateOran = ({
	id,
	name,
	isShared,
}: IEditPresetNameDTO): Promise<void> => {
	return client.post(`${presets}/${id}/check-for-update/shareable`, { name, isShared });
};

export const checkPresetForUpdateIsShared = ({
	id,
	name,
	isShared,
}: IEditPresetNameDTO): Promise<void> => {
	return client.post(`${presets}/${id}/check-for-update/shareable`, { name, isShared });
};

export const checkPresetForCreate = ({ name }: Pick<IEditPresetNameDTO, 'name' | 'isShared'>): Promise<void> => {
	return client.post(`${presets}/check-for-create`, { name });
};

export const checkPresetForCreateOran = ({
	name,
	isShared,
}: Pick<IEditPresetNameDTO, 'name' | 'isShared'>): Promise<void> => {
	return client.post(`${presets}/check-for-create/shareable`, { name, isShared });
};

export const updatePresetName = ({ id, name, isShared }: IEditPresetNameDTO): Promise<void> => {
	return client.put(`${presets}/${id}`, { name, isShared });
};

export const updatePreset = (data: IEditPresetDTO): Promise<TPresetTypeDTO> => {
	return client.put(`${presets}/${data.id}/update`, { ...data });
};

export const getOptionsCells = (data: ISitesOptionsDTO): Promise<IGroupSelectOptions[]> => client.get(sitesKpi, {
	params: {
		...data,
	},
	paramsSerializer: (params: ParamsSerializerOptions) => qs.stringify(params, { indices: false }),
});

export const getOptionsCellsOran = (data: ISitesOptionsDTO): Promise<IGroupSelectOptionsOranDto[]> => {
	return client.get(sitesKpiOran, {
		params: {
			...data,
		},
		paramsSerializer: (params: ParamsSerializerOptions) => qs.stringify(params, { indices: false }),
	});
};

export const getOptionsGranularities = (data: IGranularitiesOptionsDTO): Promise<TNameTitle<EGranularityValue>[]> => {
	return client.get(granularitiesKpiOran, {
		params: {
			...data,
		},
		paramsSerializer: (params: ParamsSerializerOptions) => qs.stringify(params, { indices: false }),
	});
};

export const getAutomaticGranularity = (data: IGranularitiesOptionsDTO): Promise<TNameTitle<EGranularityValue>> => {
	return client.get(`${granularitiesKpiOran}/automatic`, {
		params: {
			...data,
		},
		paramsSerializer: (params: ParamsSerializerOptions) => qs.stringify(params, { indices: false }),
	});
};

export const getAvailableGranularityDrillUp =
	(data: IAvailableGranularityDrillUpOptionsDTO): Promise<TNameTitle<EGranularityValue>> => {
		return client.get(`${granularitiesKpiOran}/drill-up`, {
			params: {
				...data,
			},
			paramsSerializer: (params: ParamsSerializerOptions) => qs.stringify(params, { indices: false }),
		});
	};

export const getOptionsKpi = (
	vendor: TVendorsValues,
	orgParams: Partial<IOrganizationId> = {},
): Promise<IOptionsKpiDto[]> => {
	return client.get(`${URL.kpis}`, {
		params: {
			vendor,
			...orgParams,
		},
		paramsSerializer: (params: ParamsSerializerOptions) => qs.stringify(params, { indices: false }),
	});
};

export const getOptionsKpiLineType = (
	vendor: TVendorsValues,
	orgParams: Partial<IOrganizationId> = {},
): Promise<IOptionsKpiDto[]> => {
	return client.get(`${URL.kpisLineType}`, {
		params: {
			vendor,
			...orgParams,
		},
		paramsSerializer: (params: ParamsSerializerOptions) => qs.stringify(params, { indices: false }),
	});
};

export const getKpiVendors = (params: IOrganizationId): Promise<DefaultVendorOptions[]> => {
	return client.get(vendorsKpi, { params });
};

export const setKpiVendor = ({ vendor }: IVendor): Promise<void> => {
	return client.post(vendorsKpi, { vendor });
};

export const runCorrelation = ({ set1, set2 }: ICorrelateDTO): Promise<void> => client.post(correlationKpi, {
	set1,
	set2,
});

export const startProgress = (): Promise<void> => {
	return client.post(chartsKpiProgress);
};

export const runProcessKpi = (data: IKpiChartsProgressDTO): Promise<string> => {
	return client.post(chartsKpiRun, data);
};

export const getProgress = (id: string): Promise<IProgressKpiDTO> => {
	return client.get(`${chartsKpiProgress}/${id}`);
};

export const getKpiCharts = (data: IKpiChartsDTO): Promise<IChart> => {
	return client.post(chartsKpi, {
		aggregationType: data.aggregationType,
		from: data.from,
		to: data.to,
		kpiIds: data.kpiIds,
		sites: data.sites,
		timeAggregationType: data.timeAggregationType,
		vendor: data.vendor,
		progressId: data.progressId,
		granularity: data.granularity,
		isAutomaticGranularity: data.isAutomaticGranularity,
	});
};

export const exportKpiCharts = (data: Array<IExportKpiDTO>): Promise<void> => client.post(exportKpi, data);

export const exportKpiChartsFromParams = (data: IExportKpiFromParamsDTO): Promise<void> => {
	return client.post(`${exportKpi}/details`, data);
};

export const exportCompareKpiCharts = (data: Array<IExportKpiCompareDTO>): Promise<void> => {
	return client.post(exportCompareKpi, data);
};

export const exportCorrelateKpiCharts = (data: Array<IExportKpiCorrelateDTO>): Promise<void> =>
	client.post(exportCorrelateKpi, data);

export const getKpiSettings = (vendor: string, orgParams: Partial<IOrganizationId> = {}): Promise<IMaxSelection> => {
	return client.get(settingsKpi, {
		params: {
			vendor,
			...orgParams,
		},
	});
};

export const getKpiGroup = (vendor: string, orgParams: Partial<IOrganizationId> = {}): Promise<IKpiTreeGroup> => {
	return client.get(groupedKpi, {
		params: {
			vendor,
			...orgParams,
		},
		paramsSerializer: (params: ParamsSerializerOptions) => qs.stringify(params, { indices: false }),
	});
};

export const getKpiGroupLineType = (
	vendor: string,
	orgParams: Partial<IOrganizationId> = {},
): Promise<IKpiTreeGroup> => {
	return client.get(kpisGroupedLineType, {
		params: {
			vendor,
			...orgParams,
		},
		paramsSerializer: (params: ParamsSerializerOptions) => qs.stringify(params, { indices: false }),
	});
};

export const getCountersGroup = (vendor: string, orgParams: Partial<IOrganizationId> = {}): Promise<IKpiTreeGroup> => {
	return client.get(groupedCounters, {
		params: {
			vendor,
			...orgParams,
		},
		paramsSerializer: (params: ParamsSerializerOptions) => qs.stringify(params, { indices: false }),
	});
};

export const getDynamicDates = (params: IOrganizationId): Promise<INameTitle[]> => {
	return client.get(dynamicDates, { params });
};

export const getDynamicDatesPreview = (data: IDynamicDatesRange): Promise<IDynamicDatesRangeDTO> => {
	return client.get(`${dynamicDates}/preview`, {
		params: data,
		paramsSerializer: (params: ParamsSerializerOptions) => qs.stringify(params, { indices: false }),
	});
};
